import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setTokenValid(true);
    } catch (error) {
      console.error('Invalid token:', error);
      toast.error('Token inválido o expirado');
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    // Validar requisitos de contraseña segura
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('La contraseña debe incluir al menos una mayúscula');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      toast.error('La contraseña debe incluir al menos una minúscula');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error('La contraseña debe incluir al menos un número');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword)) {
      toast.error('La contraseña debe incluir al menos un símbolo (!@#$%^&*...)');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });

      setSuccess(true);
      toast.success('Contraseña restablecida correctamente');
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5ff 100%)' }}>
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: '#7012b2' }} />
            <p className="verdana-regular text-gray-600">Verificando token...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5ff 100%)' }}>
        <Card className="max-w-md w-full mx-4" data-testid="invalid-token-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <XCircle className="w-16 h-16 text-red-500" />
            </div>
            <CardTitle className="gotham-bold text-2xl" style={{ color: '#272b96' }}>
              Token Inválido
            </CardTitle>
            <CardDescription className="verdana-regular text-gray-600">
              El enlace de restablecimiento es inválido o ha expirado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/')}
              className="w-full btn-viubux"
              data-testid="request-new-link-btn"
            >
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5ff 100%)' }}>
        <Card className="max-w-md w-full mx-4" data-testid="success-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <CardTitle className="gotham-bold text-2xl" style={{ color: '#272b96' }}>
              ¡Contraseña Restablecida!
            </CardTitle>
            <CardDescription className="verdana-regular text-gray-600">
              Tu contraseña ha sido actualizada correctamente. Redirigiendo al inicio de sesión...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5ff 100%)' }}>
      <Card className="max-w-md w-full mx-4" data-testid="reset-password-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-3xl text-center" style={{ color: '#272b96' }}>
            Restablecer Contraseña
          </CardTitle>
          <CardDescription className="verdana-regular text-center text-gray-600">
            Ingresa tu nueva contraseña
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="verdana-regular text-gray-700 text-sm mb-2 block">
                Nueva Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="new-password-input"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full pl-10 pr-12 py-3 rounded-lg border verdana-regular focus:outline-none focus:ring-2"
                  style={{ borderColor: '#e5e7eb', '--tw-ring-color': '#7012b2' }}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-new-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="verdana-regular text-gray-700 text-sm mb-2 block">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="confirm-password-input"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="w-full pl-10 pr-12 py-3 rounded-lg border verdana-regular focus:outline-none focus:ring-2"
                  style={{ borderColor: '#e5e7eb', '--tw-ring-color': '#7012b2' }}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-confirm-password-visibility"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="verdana-regular text-red-600 text-sm">
                  Las contraseñas no coinciden
                </p>
              </div>
            )}

            <Button
              data-testid="submit-new-password-btn"
              type="submit"
              className="w-full btn-viubux"
              disabled={loading || newPassword !== confirmPassword}
            >
              {loading ? 'Procesando...' : 'Restablecer Contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
